import React from "react";
import { ChevronRight} from "lucide-react";

/* =========================================================
   SOCIAL BUTTON
========================================================= */

function SocialButton({ href, icon: Icon, theme }) {
  if (!href || !Icon) return null;

  const isExternal =
    href.startsWith("http://") ||
    href.startsWith("https://");

  return (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={
        isExternal
          ? "noopener noreferrer"
          : undefined
      }
      className={`
        flex
        h-14
        w-14
        items-center
        justify-center
        rounded-full
        border
        ${theme.swatches.light.border}
        bg-white/70
        ${theme.swatches.primary.text}
        shadow-sm
        backdrop-blur-sm
        transition-all
        duration-300
        hover:-translate-y-1
        hover:scale-110
      `}
    >
      <Icon size={24} />
    </a>
  );
}

/* =========================================================
   MAIN CARD
========================================================= */

export default function Card2({ data }) {
  /* =======================================================
     data.selectedTheme
  ======================================================= */
 

  return (
    <main
      className={`
        relative
        min-h-screen
        overflow-hidden
        bg-cover
        bg-center
        bg-no-repeat
        px-4
        py-10
        ${data?.selectedTheme?.swatches?.light.bg}
      `}

    >

      {/* =================================================
          BACKGROUND DECORATION
      ================================================= */}

      <div
        className={`
          pointer-events-none
          absolute
          left-0
          top-0
          h-72
          w-72
          rounded-full
          ${data?.selectedTheme?.swatches?.primary.bg}
          opacity-20
          blur-3xl
        `}
      />

      <div
        className={`
          pointer-events-none
          absolute
          right-0
          top-1/4
          h-72
          w-72
          rounded-full
          ${data?.selectedTheme?.swatches?.secondary.bg}
          opacity-30
          blur-3xl
        `}
      />

      <div
        className={`
          pointer-events-none
          absolute
          bottom-0
          left-1/4
          h-72
          w-72
          rounded-full
          ${data?.selectedTheme?.swatches?.light.bg}
          opacity-60
          blur-3xl
        `}
      />

      {/* =================================================
          CONTENT
      ================================================= */}

      <div
        className="
          relative
          mx-auto
          flex
          w-full
          max-w-xl
          flex-col
          items-center
        "
      >

        {/* =================================================
            LOGO
        ================================================= */}

        <img
          src={
            data?.photo?.url ||
            "/logo512.png"
          }
          alt={
            data?.displayName ||
            "Logo"
          }
          className="
mb-4
  h-48
  w-48
  rounded-full
  object-cover
  drop-shadow-xl
  sm:h-48
  sm:w-48
          "
        />

        {/* =================================================
            BRAND NAME
        ================================================= */}

        <h1
          className={`
            text-center
            text-3xl
            font-semibold
            tracking-wide
            ${data?.selectedTheme?.swatches?.primary.text}
            sm:text-4xl
          `}
        >
          {data?.displayName ||
            "Your Brand Name"}
        </h1>

        {/* Divider */}

        <div
          className={`
            mt-2
            h-px
            w-36
            bg-gradient-to-r
            from-transparent
            via-current
            to-transparent
            ${data?.selectedTheme?.swatches?.primary.text}
          `}
        />

        {/* =================================================
            TAGLINE
        ================================================= */}

        <div className="mt-6 text-center">

          <p
            className={`
              font-serif
              text-3xl
              italic
              ${data?.selectedTheme?.swatches?.primary.text}
              sm:text-4xl
            `}
          >
            {data?.tagline ||
              "Tagline goes here"}
          </p>

          <p
            className={`
              mt-1
              text-xl
              tracking-wide
              ${data?.selectedTheme?.swatches?.primary.text}
              opacity-80
              sm:text-2xl
            `}
          >
            {data?.bio ||
              "Bio goes here"}
          </p>

        </div>

        {/* =================================================
            MAIN LINKS
        ================================================= */}

        {data.filteredLinks.length > 0 && (
          <div
            className="
              mt-10
              flex
              w-full
              flex-col
              gap-4
            "
          >

            {data.filteredLinks.map(
              (link) => {
                const Icon =
                  link.icon;

                let href = data?.platformLinks?.[link.id];

                    if (link.id === "phone" && href) {
                      href = `tel:${href}`;
                    }

                    if (link.id === "email" && href) {
                      href = `mailto:${href}`;
                    }

                if (
                  !href ||
                  !Icon
                ) {
                  return null;
                }

                const isExternal =
                  href.startsWith(
                    "http://"
                  ) ||
                  href.startsWith(
                    "https://"
                  );

                return (
                  <a
                    key={link.id}
                    href={href}
                    target={
                      isExternal
                        ? "_blank"
                        : undefined
                    }
                    rel={
                      isExternal
                        ? "noopener noreferrer"
                        : undefined
                    }
                    className={`
                      group
                      flex
                      items-center
                      overflow-hidden
                      rounded-full
                      border
                      ${data?.selectedTheme?.swatches?.primary.border}
                      bg-white/70
                      shadow-lg
                      backdrop-blur-sm
                      transition-all
                      duration-300
                      hover:-translate-y-1
                      hover:shadow-xl
                    `}
                  >

                    {/* =================================
                        ICON
                    ================================= */}

                    <div
                      className={`
                        flex
                        h-20
                        w-20
                        shrink-0
                        items-center
                        justify-center
                        rounded-r-[45px]
                        bg-gradient-to-br
                        ${data?.selectedTheme?.swatches?.primary.bg}
                        ${data?.selectedTheme?.swatches?.secondary.bg}
                        ${data?.selectedTheme?.swatches?.light.text}
                        shadow-lg
                        transition-transform
                        duration-300
                        group-hover:scale-105
                      `}
                    >
                      <Icon
                        size={32}
                      />
                    </div>

                    {/* =================================
                        TEXT
                    ================================= */}

                    <div
                      className="
                        flex
                        flex-1
                        flex-col
                        px-5
                      "
                    >

                      <span
                        className={`
                          text-lg
                          font-bold
                          tracking-wide
                          ${data?.selectedTheme?.swatches?.primary.text}
                        `}
                      >
                        {link.title ||
                          link.label}
                      </span>

                      {link.subtitle && (
                        <span
                          className={`
                            mt-1
                            text-sm
                            ${data?.selectedTheme?.swatches?.primary.text}
                            opacity-70
                          `}
                        >
                          {
                            link.subtitle
                          }
                        </span>
                      )}

                    </div>

                    {/* =================================
                        ARROW
                    ================================= */}

                    <div
                      className={`
                        pr-5
                        ${data?.selectedTheme?.swatches?.primary.text}
                        transition-transform
                        duration-300
                        group-hover:translate-x-1
                      `}
                    >
                      <ChevronRight
                        size={28}
                      />
                    </div>

                  </a>
                );
              }
            )}

          </div>
        )}

        {/* =================================================
            SOCIAL ICONS
        ================================================= */}

        {data.socialLinks.length > 0 && (
          <div
            className="
              mt-10
              flex
              flex-wrap
              items-center
              justify-center
              gap-4
            "
          >

            {data.socialLinks.map(
              (link) => {

                const Icon =
                  link.icon;

                const href =
                  data?.platformLinks?.[
                    link.id
                  ];

                if (
                  !href ||
                  !Icon
                ) {
                  return null;
                }

                return (
                  <SocialButton
                    key={link.id}
                    href={href}
                    icon={Icon}
                    theme={data.selectedTheme}
                  />
                );
              }
            )}

          </div>
        )}

        {/* =================================================
            FOOTER
        ================================================= */}

        <div
          className="
            mt-10
            pb-6
            text-center
          "
        >

          <p
            className={`
              font-serif
              text-2xl
              italic
              ${data?.selectedTheme?.swatches?.primary.text}
            `}
          >
            Thank you for
            supporting ♡
          </p>

          <p
            className={`
              mt-3
              text-xs
              tracking-[0.4em]
              ${data?.selectedTheme?.swatches?.primary.text}
              opacity-70
            `}
          >
            {data?.displayName ||
              "ZEEDBIO"}
          </p>

        </div>

      </div>
    </main>
  );
}